<template>
  <header class="relative flex-shrink-0 flex items-center justify-between h-14 px-4 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700 z-20">

    <!-- Left: sidebar toggle + Today pill + arrows + month label -->
    <div class="flex items-center gap-1">
      <button
        @click="emit('toggle-sidebar')"
        class="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors mr-1"
        aria-label="Toggle sidebar"
        title="Toggle sidebar"
      >
        <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
        </svg>
      </button>

      <button
        @click="emit('today')"
        class="px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors mr-1"
      >
        Today
      </button>

      <button
        @click="emit('prev')"
        class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label="Previous"
      >
        <svg class="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>

      <button
        @click="emit('next')"
        class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label="Next"
      >
        <svg class="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
      </button>

      <h1 class="text-xl font-normal text-gray-700 dark:text-gray-200 select-none ml-2">
        {{ monthYearLabel }}
      </h1>
    </div>

    <!-- Search bar (replaces header content while open) -->
    <Transition name="search-bar">
      <div
        v-if="searchOpen"
        class="absolute inset-0 flex items-center gap-2 h-14 px-4 bg-white dark:bg-[#1C2128]"
      >
        <button
          @click="closeSearch"
          class="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Close search"
          title="Close search"
        >
          <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
        </button>

        <div class="relative flex-1 max-w-xl" ref="searchBoxRef">
          <svg class="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/>
          </svg>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            placeholder="Search in calendar"
            class="w-full pl-10 pr-16 py-2 text-sm rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            @keydown.esc="closeSearch"
            @keydown.down.prevent="moveActive(1)"
            @keydown.up.prevent="moveActive(-1)"
            @keydown.enter.prevent="onEnter"
          />

          <!-- Loading spinner while a search is in flight -->
          <svg
            v-if="searchLoading"
            class="absolute right-9 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin"
            fill="none" viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>

          <!-- Clear button once a search has settled -->
          <button
            v-else-if="searchQuery"
            @click="clearQuery"
            class="absolute right-9 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Clear search"
          >
            <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>

          <!-- Filters chevron — always the rightmost control inside the field.
               Turns blue with a dot badge once a date filter is applied, so
               it stays visible after the panel itself is closed. -->
          <button
            @click="filterOpen = !filterOpen"
            class="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full transition-colors"
            :class="hasActiveFilter
              ? 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50'
              : 'hover:bg-gray-200 dark:hover:bg-gray-700'"
            :aria-label="hasActiveFilter ? 'Search filters (1 active)' : 'Search filters'"
            :title="hasActiveFilter ? 'Search filters (1 active)' : 'Search filters'"
          >
            <svg
              class="w-4 h-4"
              :class="hasActiveFilter ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/>
            </svg>
            <span
              v-if="hasActiveFilter"
              class="absolute top-0 right-0 w-2 h-2 rounded-full bg-blue-600 ring-2 ring-white dark:ring-[#1C2128]"
            ></span>
          </button>

          <!-- Autofill dropdown -->
          <Transition name="dropdown">
            <div
              v-if="showAutofillDropdown"
              class="absolute left-0 top-full mt-2 w-full bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl z-50 overflow-hidden max-h-80 overflow-y-auto"
            >
              <div v-if="hasActiveFilter" class="px-4 pt-3 pb-1">
                <span class="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full">
                  {{ activeFilterLabel }}
                  <button
                    @click="onResetDateFilter"
                    class="w-4 h-4 flex items-center justify-center rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                    aria-label="Clear filter"
                    title="Clear filter"
                  >
                    <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </span>
              </div>

              <div v-if="searchLoading" class="px-4 py-3 text-sm text-gray-400 dark:text-gray-500">
                Searching…
              </div>
              <div v-else-if="searchResults.length === 0" class="flex flex-col items-center justify-center py-8 px-4 text-center">
                <div class="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
                  <svg class="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.5 9.5l3 3m0-3l-3 3"/>
                  </svg>
                </div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-300">No matching events</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Try a different search term</p>
              </div>
              <CalendarSearchResultRow
                v-for="(result, index) in searchResults"
                :key="result.id"
                :ref="el => setResultRef(el, index)"
                :result="result"
                :active="index === activeIndex"
                @click="selectResult(result)"
                @mouseenter="activeIndex = index"
              />
            </div>
          </Transition>

          <!-- Filter panel -->
          <Transition name="dropdown">
            <CalendarSearchDateFilter
              v-if="filterOpen"
              class="absolute left-0 top-full mt-2 w-full"
              :preset="dateFilter.preset"
              :from="dateFilter.from"
              :to="dateFilter.to"
              @apply="onApplyDateFilter"
              @reset="onResetDateFilter"
            />
          </Transition>
        </div>
      </div>
    </Transition>

    <!-- Right: search + settings gear + view dropdown -->
    <div class="flex items-center gap-2">
      <!-- Search -->
      <button
        @click="openSearch"
        class="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label="Search"
        title="Search"
      >
        <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/>
        </svg>
      </button>

      <!-- Settings gear -->
      <button
        @click="emit('open-settings')"
        class="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label="Calendar settings"
      >
        <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
      </button>

      <!-- View dropdown pill -->
      <div class="relative" ref="dropdownRef">
        <button
          @click="dropdownOpen = !dropdownOpen"
          class="flex items-center gap-1 px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          {{ viewLabel }}
          <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>

        <!-- Dropdown menu -->
        <Transition name="dropdown">
          <div
            v-if="dropdownOpen"
            class="absolute right-0 top-full mt-1 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 overflow-hidden min-w-[130px]"
          >
            <button
              v-for="v in views"
              :key="v.value"
              @click="selectView(v.value)"
              class="w-full px-4 py-2.5 text-sm text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/70 transition-colors"
              :class="currentView === v.value
                ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50/50 dark:bg-blue-900/10'
                : 'text-gray-700 dark:text-gray-200'"
            >
              {{ v.label }}
              <svg
                v-if="currentView === v.value"
                class="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0"
                fill="currentColor" viewBox="0 0 20 20"
              >
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
              </svg>
            </button>
          </div>
        </Transition>
      </div>

      <!-- Full screen -->
      <div class="relative" ref="fullscreenRef">
        <button
          @click="onFullscreenButton"
          class="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          :aria-label="isFullscreen ? 'Exit full screen' : 'Full screen'"
          :title="isFullscreen ? 'Exit full screen' : 'Full screen'"
        >
          <svg v-if="!isFullscreen" class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
          </svg>
          <svg v-else class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 9L4 4m5 5V5m0 4H5m10 0l5-5m-5 5V5m0 4h4M9 15l-5 5m5-5v4m0-4H5m10 0l5 5m-5-5v4m0-4h4"/>
          </svg>
        </button>

        <Transition name="dropdown">
          <div
            v-if="fullscreenDropdownOpen"
            class="absolute right-0 top-full mt-1 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 overflow-hidden w-64"
          >
            <button
              @click="selectFullscreen('page')"
              class="w-full px-4 py-3 text-left flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-800/70 transition-colors"
            >
              <svg class="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 9h16M9 9v11"/>
              </svg>
              <span class="min-w-0">
                <span class="block text-sm font-medium text-gray-700 dark:text-gray-200">Full Page</span>
                <span class="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">Header, mini calendar and grid in full screen</span>
              </span>
            </button>
            <button
              @click="selectFullscreen('main')"
              class="w-full px-4 py-3 text-left flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-800/70 transition-colors"
            >
              <svg class="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              <span class="min-w-0">
                <span class="block text-sm font-medium text-gray-700 dark:text-gray-200">Main Calendar</span>
                <span class="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">Only the calendar grid in full screen</span>
              </span>
            </button>
          </div>
        </Transition>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, watch, computed, nextTick, onMounted, onUnmounted } from 'vue'
import { useRequest } from 'composables/useRequest.js'
import CalendarSearchResultRow from './CalendarSearchResultRow.vue'
import CalendarSearchDateFilter from './CalendarSearchDateFilter.vue'

const props = defineProps({
  currentView:      { type: String, required: true },
  selectedDate:     { type: Date,   default: null  },
  connectionStatus: { type: String, default: 'connecting' },
  isFullscreen:     { type: Boolean, default: false },
})

const emit = defineEmits([
  'prev', 'next', 'today', 'view-change', 'open-settings', 'fullscreen', 'toggle-sidebar',
  'select-search-result', 'search-submit', 'close-search-overlay', 'search-filter-change',
])

const { axiosRequest } = useRequest()

const searchOpen       = ref(false)
const searchQuery      = ref('')
const searchInputRef   = ref(null)
const searchResults    = ref([])
const searchLoading    = ref(false)
const activeIndex      = ref(-1)
// True right after Enter submits the full-results overlay, so the small
// autofill dropdown doesn't float on top of it until the user types again.
const suppressDropdown = ref(false)

// Date filter (chevron menu) — either an Upcoming/Past preset or an explicit
// from/to range. Applied to both the live autofill fetch and full search-submit.
const filterOpen  = ref(false)
const searchBoxRef = ref(null)
const dateFilter  = ref({ preset: null, from: '', to: '' })
const hasActiveFilter = computed(() =>
  Boolean(dateFilter.value.preset || dateFilter.value.from || dateFilter.value.to)
)

function formatFilterDate(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const activeFilterLabel = computed(() => {
  const { preset, from, to } = dateFilter.value
  if (preset === 'upcoming') return 'Upcoming'
  if (preset === 'past') return 'Past'
  if (from && to) return `${formatFilterDate(from)} – ${formatFilterDate(to)}`
  if (from) return `From ${formatFilterDate(from)}`
  if (to) return `Until ${formatFilterDate(to)}`
  return ''
})

// The autofill dropdown and the filter panel are mutually exclusive.
const showAutofillDropdown = computed(() =>
  searchQuery.value.trim().length > 0 && !suppressDropdown.value && !filterOpen.value
)

function dateFilterParams() {
  const params = {}
  if (dateFilter.value.from) params.from_date = dateFilter.value.from
  if (dateFilter.value.to) params.to_date = dateFilter.value.to
  return params
}

let searchDebounceTimer = null
let searchRequestToken  = 0

async function openSearch() {
  searchOpen.value = true
  await nextTick()
  searchInputRef.value?.focus()
}

function closeSearch() {
  searchOpen.value = false
  searchQuery.value = ''
  searchResults.value = []
  activeIndex.value = -1
  suppressDropdown.value = false
  filterOpen.value = false
  dateFilter.value = { preset: null, from: '', to: '' }
  clearTimeout(searchDebounceTimer)
  emit('close-search-overlay')
}

function clearQuery() {
  searchQuery.value = ''
}

async function runSearch(query) {
  const token = ++searchRequestToken
  searchLoading.value = true
  const params = new URLSearchParams({ query, ...dateFilterParams() })
  const res = await axiosRequest('GET', `/api/v1/calendar/bookings/search?${params.toString()}`, {}, false)
  if (token !== searchRequestToken) return // a newer search superseded this one
  searchResults.value = (!res.js_error && res.response?.success) ? res.response.data : []
  activeIndex.value = -1
  searchLoading.value = false
}

watch(searchQuery, (query) => {
  clearTimeout(searchDebounceTimer)
  const trimmed = query.trim()
  activeIndex.value = -1
  suppressDropdown.value = false
  filterOpen.value = false
  if (!trimmed) {
    searchResults.value = []
    emit('close-search-overlay')
    return
  }
  searchDebounceTimer = setTimeout(() => runSearch(trimmed), 300)
})

function onEnter() {
  // A highlighted suggestion wins — Enter selects it, same as clicking it.
  if (activeIndex.value !== -1 && searchResults.value[activeIndex.value]) {
    selectActive()
    return
  }
  const trimmed = searchQuery.value.trim()
  if (!trimmed) return
  suppressDropdown.value = true
  emit('search-submit', { query: trimmed, ...dateFilterParams() })
}

function onApplyDateFilter(filter) {
  dateFilter.value = filter
  filterOpen.value = false
  refreshActiveSearch()
}

function onResetDateFilter() {
  dateFilter.value = { preset: null, from: '', to: '' }
  filterOpen.value = false
  refreshActiveSearch()
}

// Re-runs whichever search is currently active under the (possibly just
// changed) date filter: the live autofill dropdown, and/or — via the emitted
// event — a full-results overlay the parent already has open.
function refreshActiveSearch() {
  const trimmed = searchQuery.value.trim()
  if (!trimmed) return
  runSearch(trimmed)
  emit('search-filter-change', { query: trimmed, ...dateFilterParams() })
}

let resultRefs = []
function setResultRef(el, index) {
  resultRefs[index] = el
}

function moveActive(step) {
  if (searchResults.value.length === 0) return
  const count = searchResults.value.length
  // With nothing highlighted yet, the first ArrowDown should land on the
  // first item and the first ArrowUp on the last — plain modulo arithmetic
  // on a -1 sentinel doesn't give that for both directions.
  if (activeIndex.value === -1) {
    activeIndex.value = step > 0 ? 0 : count - 1
  } else {
    activeIndex.value = (activeIndex.value + step + count) % count
  }
  resultRefs[activeIndex.value]?.$el?.scrollIntoView?.({ block: 'nearest' })
}

function selectActive() {
  if (activeIndex.value === -1 || !searchResults.value[activeIndex.value]) return
  selectResult(searchResults.value[activeIndex.value])
}

function selectResult(result) {
  emit('select-search-result', result)
  closeSearch()
}

const dropdownOpen = ref(false)
const dropdownRef  = ref(null)

const fullscreenDropdownOpen = ref(false)
const fullscreenRef          = ref(null)

function onFullscreenButton() {
  if (props.isFullscreen) {
    emit('fullscreen', 'exit')
  } else {
    fullscreenDropdownOpen.value = !fullscreenDropdownOpen.value
  }
}

function selectFullscreen(mode) {
  emit('fullscreen', mode)
  fullscreenDropdownOpen.value = false
}

const views = [
  { value: 'day',   label: 'Day'    },
  { value: 'week',  label: 'Week'   },
  { value: 'month', label: 'Month'  },
  { value: 'year',  label: 'Year'   },
  { value: '4days', label: '4 Days' },
]

const monthYearLabel = computed(() => {
  const d = props.selectedDate ?? new Date()
  if (props.currentView === 'year') {
    return String(d.getFullYear())
  }
  if (props.currentView === '4days') {
    const end = new Date(d); end.setDate(d.getDate() + 3)
    const startFmt = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const endFmt   = end.toLocaleDateString('en-US', {
      month:  d.getMonth() === end.getMonth() ? undefined : 'short',
      day:    'numeric',
      year:   'numeric',
    })
    return `${startFmt} – ${endFmt}`
  }
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
})

const viewLabel = computed(() => views.find(v => v.value === props.currentView)?.label ?? 'Week')

function selectView(value) {
  emit('view-change', value)
  dropdownOpen.value = false
}

function onClickOutside(e) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
    dropdownOpen.value = false
  }
  if (fullscreenRef.value && !fullscreenRef.value.contains(e.target)) {
    fullscreenDropdownOpen.value = false
  }
  if (searchBoxRef.value && !searchBoxRef.value.contains(e.target)) {
    filterOpen.value = false
  }
}

onMounted(()  => document.addEventListener('click', onClickOutside, true))
onUnmounted(() => document.removeEventListener('click', onClickOutside, true))
</script>

<style scoped>
.dropdown-enter-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.dropdown-leave-active { transition: opacity 0.08s ease, transform 0.08s ease; }
.dropdown-enter-from,
.dropdown-leave-to    { opacity: 0; transform: translateY(-4px); }

.search-bar-enter-active { transition: opacity 0.12s ease, transform 0.12s ease; }
.search-bar-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.search-bar-enter-from,
.search-bar-leave-to     { opacity: 0; transform: translateY(-4px); }
</style>
